// Shared bits of the full-screen unscheduled-visit flow (KAN-765 / RD-UI-060):
// Customer -> Reason -> the Log Job form. Steps 1-2 live in
// UnscheduledVisitFlow.js (a full-screen overlay on the route board); step 3 IS
// the Log Job route page, which wears the same kit header while it is entered
// from this flow (UnscheduledLogJobHeader.js). The two halves talk through
// router state only, so what Log Job saves is byte-identical to before:
//
//   board -> /log-job/:id?context=&reason=[&sameDayOverride=1]
//            state { returnTo, unscheduledFlow: true }
//   Log Job "Back" -> returnTo with state { resumeUnscheduled: { ... } }, which
//            the launcher reads to reopen the flow on the Reason step.

/** Router-state flag set by the launcher: this Log Job was entered from the flow. */
export const UNSCHEDULED_FLOW_STATE_KEY = 'unscheduledFlow';
/** Router-state key holding the picked customer's name/address so Log Job's very first frame (and its route fallback) can draw the rail's identity card (KAN-771). */
export const UNSCHEDULED_IDENTITY_STATE_KEY = 'unscheduledIdentity';
/** Router-state key Log Job's Back writes so the launcher reopens on the Reason step. */
export const UNSCHEDULED_RESUME_STATE_KEY = 'resumeUnscheduled';

/** The three steps, shared by the flow (1-2) and Log Job's header (3). */
export function unscheduledFlowSteps(intl) {
  return [
    { key: 'customer', label: intl.formatMessage({ id: 'worker.unscheduled.wizard.customer', defaultMessage: 'Customer' }) },
    { key: 'reason', label: intl.formatMessage({ id: 'worker.unscheduled.wizard.reason', defaultMessage: 'Reason' }) },
    { key: 'log', label: intl.formatMessage({ id: 'worker.unscheduled.wizard.log', defaultMessage: 'Log the visit' }) },
  ];
}

/**
 * Where the flow sends the worker once a reason is chosen. Same URL contract
 * the modal used (context from the precheck, the reason, the same-day override)
 * plus the flag that makes Log Job wear the flow's header.
 */
export function buildLogJobTarget({ customerId, computedContext, reason, sameDayOverride, returnTo, identity = null }) {
  const params = new URLSearchParams({ context: computedContext, reason });
  if (sameDayOverride) params.set('sameDayOverride', '1');
  return {
    path: `/log-job/${customerId}?${params.toString()}`,
    state: {
      returnTo,
      [UNSCHEDULED_FLOW_STATE_KEY]: true,
      ...(identity ? { [UNSCHEDULED_IDENTITY_STATE_KEY]: identity } : null),
    },
  };
}

/** The `{ name, address, offSeason }` identity seed from router state; null when absent. */
export function readIdentitySeed(state) {
  const i = state?.[UNSCHEDULED_IDENTITY_STATE_KEY];
  if (!i || typeof i.name !== 'string') return null;
  return { name: i.name, address: typeof i.address === 'string' ? i.address : '', offSeason: Boolean(i.offSeason) };
}

/** Router state for Log Job's Back: reopen the Reason step for this customer. */
export function buildResumeState({ customerId, reason, sameDayOverride }) {
  return {
    [UNSCHEDULED_RESUME_STATE_KEY]: {
      customerId,
      reason: reason || '',
      sameDayOverride: Boolean(sameDayOverride),
    },
  };
}

/** Reads a resume request from router state; null when there is none. */
export function readResumeRequest(state) {
  const r = state?.[UNSCHEDULED_RESUME_STATE_KEY];
  if (!r || typeof r.customerId !== 'string' || !r.customerId) return null;
  return { customerId: r.customerId, reason: typeof r.reason === 'string' ? r.reason : '', sameDayOverride: Boolean(r.sameDayOverride) };
}
